/**
 * ProgressPillStack — Vercel/Linear-style picture-in-picture pills that
 * stay visible when the user minimizes BriefProgressModal or
 * EditorialProgressModal. Consumes the existing hooks so no SSE logic
 * moves; only the presentation of the minimized state changes.
 *
 * Layout: fixed bottom-4 right-4, vertically stacked (Brief on top,
 * Editorial below) with a 8px gap. Mobile: full-width stacked bar.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowUpRight, AlertCircle, CheckCircle2, Lightbulb,
  Loader2, X, Zap,
} from "lucide-react";
import {
  useAgentTimeline,
  useBriefProgress,
} from "@/hooks/useBriefProgress";
import { useEditorialProgress } from "@/hooks/useEditorialProgress";

// ---------------- Design tokens (local, kept in sync with the app theme) ----
const T = {
  bg:            "#FFFFFF",
  border:        "#E5E9F0",
  fg:            "#0F1B2D",
  fgMuted:       "#3D4B62",
  fgSubtle:      "#5B6B82",
  rail:          "#EEF2F7",
  accent:        "#0FA9DB",
  accentSoft:    "#E6F4FB",
  success:       "#0F9F6E",
  successSoft:   "#E7F6F0",
  danger:        "#DC2626",
  dangerSoft:    "#FEF2F2",
  shadow:        "0 12px 32px rgba(15,27,45,0.10)",
};

// ---------------- Brief step labels ----------------
const BRIEF_STEP_LABEL = {
  source_universe_scanner: "Discovering sources",
  news_scanner:            "Fetching articles",
  entity_extractor:        "Extracting entities",
  brief_composer:          "Composing brief",
  brief_publisher:         "Publishing",
};

// ---------------- Editorial step labels ----------------
const EDIT_STEP_ORDER = ["scan", "evidence", "draft", "publish"];
const EDIT_STEP_LABEL = {
  scan:     "Scanning signal patterns",
  evidence: "Gathering evidence",
  draft:    "Drafting proposals",
  publish:  "Publishing",
};

// ---------------- Generic Pill ----------------
/**
 * @param icon           lucide component
 * @param title          "Generating brief"
 * @param stepLabel      "Discovering sources"
 * @param stepIndex/Count "2 of 5"
 * @param percent        0-100 completion of the whole run
 * @param status         "running" | "done" | "failed"
 * @param almostDone     >90% highlight
 * @param onExpand()     restore modal
 * @param onDismiss()    hide pill (does NOT cancel the run)
 * @param testId         "brief" | "editorial"
 */
function ProgressPill({
  icon: Icon, title, stepLabel, stepIndex, stepCount, percent, status,
  almostDone, onExpand, onDismiss, testId,
}) {
  const isDone = status === "done";
  const isFail = status === "failed";
  const StatusIcon = isFail ? AlertCircle : isDone ? CheckCircle2 : Icon;
  const iconColor = isFail ? T.danger : isDone ? T.success : T.accent;
  const bg        = isFail ? T.dangerSoft : isDone ? T.successSoft : T.bg;

  const displayTitle =
    isDone   ? (testId === "brief" ? "Brief ready" : "Proposals ready") :
    isFail   ? (testId === "brief" ? "Brief failed" : "Editorial failed") :
    almostDone ? "Almost done…" :
    title;

  return (
    <motion.div
      role="status"
      aria-live="polite"
      data-testid={`progress-pill-${testId}`}
      initial={{ opacity: 0, y: 20, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.98 }}
      transition={{ duration: 0.22, ease: "easeOut" }}
      className="w-[340px] max-w-[calc(100vw-32px)]"
      style={{
        background:  bg,
        border:      `1px solid ${isFail ? "#FBCFCF" : isDone ? "#C7EBDB" : T.border}`,
        borderRadius: 12,
        boxShadow:   T.shadow,
        padding:     "12px 14px",
        position:    "relative",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        {/* Icon column with subtle running pulse */}
        <div style={{ position: "relative", width: 28, height: 28, flexShrink: 0 }}>
          {status === "running" && (
            <motion.span
              aria-hidden
              className="absolute inset-0 rounded-full"
              style={{ background: T.accentSoft }}
              animate={{ scale: [1, 1.25, 1], opacity: [0.7, 0, 0.7] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            />
          )}
          <div
            className="absolute inset-0 rounded-full grid place-items-center"
            style={{ background: isFail ? T.dangerSoft : isDone ? T.successSoft : T.accentSoft }}
          >
            {status === "running"
              ? <Loader2 className="h-4 w-4 animate-spin" style={{ color: iconColor }} strokeWidth={2.5} />
              : <StatusIcon className="h-4 w-4" style={{ color: iconColor }} strokeWidth={2.5} />}
          </div>
        </div>

        {/* Middle — title + step + progress bar */}
        <button
          type="button"
          onClick={onExpand}
          data-testid={`progress-pill-${testId}-expand`}
          className="flex-1 min-w-0 text-left"
          style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer" }}
        >
          <div className="flex items-center gap-1.5">
            <span style={{ color: T.fg, fontSize: 13, fontWeight: 600, letterSpacing: -0.1 }}>
              {displayTitle}
            </span>
          </div>
          <div
            className="truncate"
            style={{ color: T.fgMuted, fontSize: 11, marginTop: 2, lineHeight: 1.35 }}
          >
            {isDone
              ? (testId === "brief" ? "Tap to open the brief" : "Tap to open the editorial view")
              : isFail
                ? "Tap to see the error"
                : stepLabel && stepCount
                  ? `${stepLabel} · ${stepIndex} of ${stepCount}`
                  : stepLabel || "Working…"}
          </div>
          {!isFail && (
            <div
              className="mt-2 w-full rounded-full overflow-hidden"
              style={{ height: 3, background: T.rail }}
            >
              <motion.div
                style={{
                  height: "100%",
                  background: isDone ? T.success : T.accent,
                  borderRadius: 999,
                }}
                initial={false}
                animate={{ width: `${Math.max(4, Math.min(100, percent))}%` }}
                transition={{ duration: 0.4, ease: "easeOut" }}
              />
            </div>
          )}
        </button>

        {/* Right — expand chevron */}
        <button
          type="button"
          onClick={onExpand}
          aria-label="Open modal"
          data-testid={`progress-pill-${testId}-arrow`}
          className="shrink-0 grid place-items-center rounded-md transition-colors"
          style={{ width: 28, height: 28, color: T.fgMuted, background: "transparent", border: "none", cursor: "pointer" }}
          onMouseEnter={(e) => { e.currentTarget.style.color = T.accent; e.currentTarget.style.background = T.accentSoft; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = T.fgMuted; e.currentTarget.style.background = "transparent"; }}
        >
          <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
        </button>
      </div>

      {/* Optional close (top-right absolute). Does NOT cancel the run. */}
      {onDismiss && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDismiss(); }}
          aria-label="Hide progress pill"
          data-testid={`progress-pill-${testId}-dismiss`}
          className="absolute rounded-md transition-colors"
          style={{
            top: 6, right: 6, width: 20, height: 20,
            display: "grid", placeItems: "center",
            color: T.fgSubtle, background: "transparent", border: "none", cursor: "pointer",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = T.danger; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = T.fgSubtle; }}
        >
          <X className="h-3 w-3" strokeWidth={2.2} />
        </button>
      )}
    </motion.div>
  );
}

// ---------------- Brief pill wrapper ----------------
function BriefPill() {
  const { active, restore, dismiss } = useBriefProgress();
  const timeline = useAgentTimeline(active);
  const navigate = useNavigate();
  const [autoHide, setAutoHide] = useState(false);

  // Compute progress deriving from the timeline (5 agents).
  const { percent, stepLabel, stepIndex, stepCount, status } = useMemo(() => {
    const total = timeline.length || 5;
    const done  = timeline.filter((t) => t.status === "completed").length;
    const running = timeline.findIndex((t) => t.status === "running");
    const runningIdx = running >= 0 ? running : (done === total ? total - 1 : done);
    const currentAgent = timeline[runningIdx]?.agent;
    const pct = active?.status === "done" ? 100 : Math.round(((done + (running >= 0 ? 0.4 : 0)) / total) * 100);
    return {
      percent:    pct,
      stepLabel:  BRIEF_STEP_LABEL[currentAgent] || "Working",
      stepIndex:  runningIdx + 1,
      stepCount:  total,
      status:     active?.status || "running",
    };
  }, [timeline, active?.status]);

  // Auto-hide 3s after a done/failed event.
  useEffect(() => {
    if (!active?.minimized) { setAutoHide(false); return; }
    if (active.status === "done" || active.status === "failed") {
      const t = setTimeout(() => setAutoHide(true), 3000);
      return () => clearTimeout(t);
    }
  }, [active?.minimized, active?.status]);

  const visible = !!(active && active.minimized) && !autoHide;
  if (!visible) return null;

  const onExpand = () => {
    if (active.status === "done" && active.briefId) {
      dismiss();
      navigate(`/daily-briefs/${active.briefId}`);
    } else {
      restore();
    }
  };

  return (
    <ProgressPill
      icon={Zap}
      title="Generating brief"
      stepLabel={stepLabel}
      stepIndex={stepIndex}
      stepCount={stepCount}
      percent={percent}
      status={status}
      almostDone={percent > 90 && status === "running"}
      onExpand={onExpand}
      onDismiss={dismiss}
      testId="brief"
    />
  );
}

// ---------------- Editorial pill wrapper ----------------
function EditorialPill() {
  const ep = useEditorialProgress();
  const [autoHide, setAutoHide] = useState(false);
  const navigate = useNavigate();

  // Only mount pill when a run happened AND user minimized (or the pill
  // has been auto-shown on completion for the flash-then-hide flow).
  const done   = !ep.isRunning && !!ep.endedAt && !ep.error;
  const failed = !ep.isRunning && !!ep.error;
  const running = ep.isRunning;

  const { percent, stepLabel, stepIndex, stepCount, status } = useMemo(() => {
    const states = ep.stepStates || {};
    const total = EDIT_STEP_ORDER.length;
    const completedCount = EDIT_STEP_ORDER.filter((k) => states[k]?.status === "completed").length;
    let currentKey = EDIT_STEP_ORDER.find((k) => states[k]?.status === "running");
    if (!currentKey) currentKey = EDIT_STEP_ORDER[Math.min(completedCount, total - 1)];
    const runningExists = EDIT_STEP_ORDER.some((k) => states[k]?.status === "running");
    const pct = done ? 100 : Math.round(((completedCount + (runningExists ? 0.4 : 0)) / total) * 100);
    return {
      percent:   pct,
      stepLabel: EDIT_STEP_LABEL[currentKey],
      stepIndex: EDIT_STEP_ORDER.indexOf(currentKey) + 1,
      stepCount: total,
      status:    failed ? "failed" : done ? "done" : "running",
    };
  }, [ep.stepStates, done, failed]);

  useEffect(() => {
    if (!ep.minimized) { setAutoHide(false); return; }
    if (done || failed) {
      const t = setTimeout(() => setAutoHide(true), 3000);
      return () => clearTimeout(t);
    }
  }, [ep.minimized, done, failed]);

  const visible = !!(ep.minimized && (running || done || failed)) && !autoHide;
  if (!visible) return null;

  const onExpand = () => {
    if (done) {
      ep.dismiss();
      navigate("/editorial");
    } else {
      ep.restore();
    }
  };

  return (
    <ProgressPill
      icon={Lightbulb}
      title="Drafting editorial proposals"
      stepLabel={stepLabel}
      stepIndex={stepIndex}
      stepCount={stepCount}
      percent={percent}
      status={status}
      almostDone={percent > 90 && running}
      onExpand={onExpand}
      onDismiss={ep.dismiss}
      testId="editorial"
    />
  );
}

// ---------------- Public stack ----------------
export default function ProgressPillStack() {
  return (
    <div
      data-testid="progress-pill-stack"
      className="fixed z-40 flex flex-col items-end gap-2 pointer-events-none"
      style={{ bottom: 16, right: 16 }}
    >
      <AnimatePresence>
        <div key="brief" className="pointer-events-auto"><BriefPill /></div>
        <div key="editorial" className="pointer-events-auto"><EditorialPill /></div>
      </AnimatePresence>
    </div>
  );
}
